/**
 * IdleLogout — automatyczne wylogowanie po 15 minutach bezczynności.
 *
 * Montowany TYLKO w AppLayout (chronione trasy). NIE w /login.
 *
 * Mechanizm:
 *   • Nasłuchuje: mousemove, mousedown, keydown, scroll, touchstart,
 *     visibilitychange. Throttle 5 s — bez spamovania setState.
 *   • Cross-tab sync: aktywność zapisywana do localStorage ('zur-last-activity'),
 *     praca w jednej karcie trzyma sesję we wszystkich kartach.
 *   • 60 sekund przed limitem pojawia się modal ostrzegawczy z odliczaniem.
 *
 * Standardy: NIST SP 800-63B AAL2 (≤30 min), PCI-DSS 8.1.8 (≤15 min).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@store/auth.store';

const IDLE_LIMIT_MS    = 15 * 60 * 1000;   // 15 minut
const WARN_BEFORE_MS   = 60 * 1000;         // ostrzeżenie 60 s przed wylogowaniem
const RESET_THROTTLE_MS = 5_000;
const LS_KEY           = 'zur-last-activity';

export function IdleLogout() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [warn, setWarn] = useState(false);

  const lastActivityRef = useRef<number>(Date.now());
  const warnTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResetRef    = useRef<number>(0);

  const doLogout = useCallback(() => {
    setWarn(false);
    toast('Wylogowano z powodu braku aktywności', { icon: '⏰', duration: 4000 });
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const reschedule = useCallback(() => {
    if (warnTimerRef.current)   clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

    const elapsed  = Date.now() - lastActivityRef.current;
    const warnIn   = Math.max(0, IDLE_LIMIT_MS - WARN_BEFORE_MS - elapsed);
    const logoutIn = Math.max(0, IDLE_LIMIT_MS - elapsed);

    warnTimerRef.current   = setTimeout(() => setWarn(true), warnIn);
    logoutTimerRef.current = setTimeout(doLogout, logoutIn);
  }, [doLogout]);

  const recordActivity = useCallback((opts?: { force?: boolean }) => {
    const now = Date.now();
    if (!opts?.force && now - lastResetRef.current < RESET_THROTTLE_MS) return;
    lastResetRef.current    = now;
    lastActivityRef.current = now;
    setWarn(false);
    try { localStorage.setItem(LS_KEY, String(now)); } catch { /* tryb prywatny */ }
    reschedule();
  }, [reschedule]);

  useEffect(() => {
    if (!user) return;

    // Użyj znacznika z innej karty jeśli jest świeższy
    try {
      const lsTs = Number(localStorage.getItem(LS_KEY) || 0);
      if (lsTs > lastActivityRef.current) lastActivityRef.current = lsTs;
    } catch { /* ignore */ }
    reschedule();

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    const onAny = () => recordActivity();
    events.forEach(e => window.addEventListener(e, onAny, { passive: true }));

    const onVisibility = () => {
      if (!document.hidden) recordActivity({ force: true });
    };
    document.addEventListener('visibilitychange', onVisibility);

    const onStorage = (e: StorageEvent) => {
      if (e.key !== LS_KEY || !e.newValue) return;
      const ts = Number(e.newValue);
      if (ts > lastActivityRef.current) {
        lastActivityRef.current = ts;
        setWarn(false);
        reschedule();
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      events.forEach(e => window.removeEventListener(e, onAny));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      if (warnTimerRef.current)   clearTimeout(warnTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [user, reschedule, recordActivity]);

  if (!user || !warn) return null;
  return (
    <IdleWarning
      onStay={() => recordActivity({ force: true })}
      onSignOut={doLogout}
    />
  );
}

// ─── Modal ostrzegawczy ────────────────────────────────────────────────────────

function IdleWarning({ onStay, onSignOut }: { onStay: () => void; onSignOut: () => void }) {
  const [secs, setSecs] = useState(Math.round(WARN_BEFORE_MS / 1000));

  useEffect(() => {
    const id = setInterval(() => setSecs(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-warn-title"
      className="fixed inset-0 z-[10100] flex items-center justify-center px-4"
      style={{ background: 'rgba(15,23,42,0.60)', backdropFilter: 'blur(3px)' }}
    >
      <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-3">
          {/* Ikona zegara */}
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-100 text-amber-700">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <h2 id="idle-warn-title" className="text-base font-extrabold text-foreground leading-tight">
              Sesja wygasa z powodu braku aktywności
            </h2>
            <p className="text-[12.5px] text-muted-foreground mt-1 leading-relaxed">
              Ze względów bezpieczeństwa zostaniesz wylogowany za{' '}
              <strong className="text-foreground tabular-nums">{secs}s</strong>.
              Twoja praca zostanie zachowana.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onSignOut}
            className="px-3 h-9 rounded-lg text-[12px] font-bold text-muted-foreground hover:bg-muted transition-colors"
          >
            Wyloguj teraz
          </button>
          <button
            type="button"
            onClick={onStay}
            autoFocus
            className="px-4 h-9 rounded-lg text-[12px] font-extrabold text-white bg-primary hover:bg-primary/90 transition-colors"
          >
            Pozostań zalogowany
          </button>
        </div>
      </div>
    </div>
  );
}
